/**
 * Regras de domínio da frota. **Um lugar só.**
 *
 * O status de um veículo decide a cor do marcador, o texto da etiqueta na
 * tabela, a contagem do resumo e o filtro da lista. Se cada tela derivasse o
 * seu, elas discordariam entre si — e o usuário veria "3 parados" no topo com
 * 4 linhas cinzas na lista, sem saber em qual acreditar.
 */
import type { Vehicle } from "./schemas.ts";

/**
 * Acima disto, a posição não é onde o veículo está agora: ele está despejando o
 * histórico guardado no flash. Aconteceu em campo com 22 h de atraso — um mapa
 * que ignora isso desenha o ônibus refazendo o trajeto de ontem ao vivo.
 */
export const BACKLOG_THRESHOLD_SECONDS = 60;

/** Acima disto, "parado" vira de fato parado, e não ruído do GPS. */
export const MOVING_THRESHOLD_KMH = 3;

export type VehicleStatus = "backlog" | "moving" | "idle" | "stopped" | "offline";

export interface StatusDescriptor {
  readonly id: VehicleStatus;
  /** Texto para o usuário. */
  readonly label: string;
  /**
   * Classe Tailwind do ponto/etiqueta. Uma fonte só para as cores.
   *
   * O texto tem os dois tons: `text-emerald-700` sumiria no escuro e
   * `text-emerald-300` some no claro. Os fundos `/15` funcionam nos dois.
   */
  readonly dot: string;
  readonly badge: string;
  /** Só o texto, para o ícone de conexão da lista. Dois tons, como o `badge`. */
  readonly text: string;
  /** Cor pura, para o SVG do marcador no mapa. */
  readonly color: string;
}

export const STATUS: Record<VehicleStatus, StatusDescriptor> = {
  backlog: {
    id: "backlog",
    label: "Recuperando histórico",
    dot: "bg-amber-400",
    badge: "bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-300",
    text: "text-amber-600 dark:text-amber-400",
    color: "#fbbf24",
  },
  moving: {
    id: "moving",
    label: "Em movimento",
    dot: "bg-emerald-400",
    badge: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300",
    text: "text-emerald-600 dark:text-emerald-400",
    color: "#34d399",
  },
  idle: {
    id: "idle",
    label: "Ligado, parado",
    dot: "bg-sky-400",
    badge: "bg-sky-500/15 text-sky-700 ring-sky-500/30 dark:text-sky-300",
    text: "text-sky-600 dark:text-sky-400",
    color: "#38bdf8",
  },
  stopped: {
    id: "stopped",
    label: "Desligado",
    dot: "bg-slate-400",
    badge: "bg-slate-500/15 text-slate-700 ring-slate-500/30 dark:text-slate-300",
    text: "text-slate-500 dark:text-slate-400",
    color: "#94a3b8",
  },
  offline: {
    id: "offline",
    label: "Sem comunicação",
    dot: "bg-rose-400",
    badge: "bg-rose-500/15 text-rose-700 ring-rose-500/30 dark:text-rose-300",
    text: "text-rose-600 dark:text-rose-400",
    color: "#fb7185",
  },
};

/**
 * A ordem importa: **backlog vence tudo**. Um veículo despejando histórico pode
 * parecer "em movimento" a 40 km/h, mas o que o operador precisa saber é que
 * aquilo aconteceu horas atrás.
 */
export function getVehicleStatus(vehicle: Vehicle): VehicleStatus {
  if (isBacklog(vehicle)) return "backlog";
  if (vehicle.online === false) return "offline";
  if (vehicle.ignition === false) return "stopped";
  if ((vehicle.speed ?? 0) > MOVING_THRESHOLD_KMH) return "moving";
  return "idle";
}

export function isBacklog(vehicle: Vehicle): boolean {
  return vehicle.delaySeconds != null && vehicle.delaySeconds > BACKLOG_THRESHOLD_SECONDS;
}

/** Nome de exibição, com a placa como segunda opção e o IMEI como último recurso. */
export function getVehicleLabel(vehicle: Vehicle): string {
  return vehicle.vehicle?.trim() || vehicle.plate?.trim() || vehicle.imei;
}

/**
 * Nome da base, e **"Sem base" quando não há** — nunca vazio nem "—".
 *
 * Os dois casos de ausência são diferentes no banco (equipamento sem veículo
 * vinculado, veículo sem base atribuída) e indistinguíveis para quem opera: nos
 * dois, ninguém sabe de quem aquele ponto é. Nomear a ausência é o que faz essa
 * lacuna aparecer na tela em vez de passar por célula em branco — a própria
 * migration pede isso em `vehicles.base_id` ("todo filtro por base precisa
 * oferecer 'sem base' em vez de omitir").
 */
export function getVehicleBase(vehicle: Vehicle): string {
  return vehicle.base?.trim() || "Sem base";
}

/**
 * Etiqueta do marcador no mapa: o nome do veículo e, quando ele não existe, a
 * base.
 *
 * Diferente de `getVehicleLabel` de propósito. Ali o último recurso é o IMEI,
 * porque a lista e o cabeçalho do detalhe precisam **identificar** a linha; aqui
 * quinze dígitos sobre o mapa não dizem nada a quem está olhando a frota, e a
 * etiqueta é de uma linha só (ver `.vehicle-label` no `index.css`). O IMEI
 * continua na lista de Vínculos e na seção de registro do detalhe.
 *
 * O efeito colateral é conhecido e foi aceito: vários equipamentos sem veículo
 * e sem base ficam todos com a mesma etiqueta. O que os distingue é o clique —
 * o detalhe abre pelo IMEI.
 */
export function getMapLabel(vehicle: Vehicle): string {
  return vehicle.vehicle?.trim() || getVehicleBase(vehicle);
}

export function hasPosition(vehicle: Vehicle): vehicle is Vehicle & { lat: number; lon: number } {
  return vehicle.lat != null && vehicle.lon != null;
}

/** Contagem por status, para os cartões do topo. Uma passada só. */
export function summarizeFleet(vehicles: readonly Vehicle[]): Record<VehicleStatus, number> & { total: number } {
  const totals = { backlog: 0, moving: 0, idle: 0, stopped: 0, offline: 0, total: vehicles.length };
  for (const vehicle of vehicles) totals[getVehicleStatus(vehicle)]++;
  return totals;
}

/**
 * Busca por nome, base, placa ou IMEI. Sem acento e sem caixa, porque quem
 * digita "onibus 12" espera achar "Ônibus 12".
 *
 * A **base** entrou junto com a troca de 01/11: ela passou a ser o que está
 * escrito em cada linha, e campo visível que a busca ignora é pior que campo
 * ausente — a tela diz "nenhum veículo encontrado" com a palavra à vista em
 * todas as linhas. Placa e IMEI ficam, ainda que não apareçam mais: é por eles
 * que se procura um carro específico.
 */
export function matchesSearch(vehicle: Vehicle, term: string): boolean {
  const needle = normalize(term);
  if (!needle) return true;
  return [vehicle.vehicle, vehicle.base, vehicle.plate, vehicle.imei].some((field) =>
    normalize(field ?? "").includes(needle),
  );
}

function normalize(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
}

/** Ordem estável da lista: sem isso a tabela dança a cada atualização. */
export function sortVehicles(vehicles: readonly Vehicle[]): Vehicle[] {
  return [...vehicles].sort((a, b) => getVehicleLabel(a).localeCompare(getVehicleLabel(b), "pt-BR"));
}
